/**
 * Created by Alex Xia on 12/17/2018.
 * Description:
 * Add entity screen page
 * ------ maintenance history ------
 *  05/05/2019 Yoyo change to support embedded mode data soure changed by config.actionSubject$
 *  12/06/2019 Alex change to support entity detail 360 feature
 *  13/08/2019 Alex change to split three states of sidesheet/entity detail/entity list
 *  08/21/2019 Ella Ma delete tab-set input data and add deleteTab callback
 *  09/02/2019 Alex change to implement features similar to home screen
 *  09/03/2019 Alex change to onpush strategy
 *  09/29/2019 Bowen change relationship
 *  11/8/2019 Daniel add event tab to entity screen
 *  12/6/2020 Alex remove side sheet out of entity screen
 *  Updated by Daniel on 03/31/2022, change event/activity closable.
 */
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription, forkJoin, Observable, of, Subject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { map, take, concatMap, switchMap, skip, filter, catchError, delay } from 'rxjs/operators';
import { ContextMenuService, ContextMenuComponent } from '@perfectmemory/ngx-contextmenu';
import { UUID } from 'angular2-uuid';

import { EntityDetailConfig } from '../../components/entity-detail/entity-detail.config';
import { Entity } from '../../tamalelibs/models/entity.model';
import { ScreenActionTypes } from '../../redux/actions/screen.actions';
import { getEntityScreenSelector } from '../../redux/reducers/entity-screen.reducer';
import { EntityScreenActionTypes } from '../../redux/actions/entity-screen.actions';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { HomeScreenService } from '../../services/home-screen.service';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { ResearchScreenSubComponents, ResearchScreenConfigs } from '../research-screen/research-screen.model';
import {
    ResearchScreenInitializeLayout, ResearchScreenThreadListSetGridStatus,
    ResearchScreenSetFilterList, ResearchScreenSetSortInfo,
    ResearchScreenSetFilterEnable, ResearchScreenSetFilterLogic,
    ResearchScreenSetKeyWord, ResearchScreenRefinebySetCategory,
    ResearchScreenInitializeFocus, ResearchScreenSetRelationship,
    ResearchScreenActionTypes, ResearchScreenThreadListSetGridRowModelType,
    ResearchScreenSetDisplayInfo
} from '../../redux/actions/research-screen.actions';
import { ResearchViewInfo, FinderInfo, FilterInfo } from '../../pages/research/research.view-model';
import { FinderSetting, ResearchFocusStatus } from '../finder/finder.view-model';
import { EntityBrief } from '../../tamalelibs/models/entity-brief.model';
import { RelationshipTreeService } from '../../services/relationship-tree.service';
import { ResearchHelperService } from '../../services/research-helper.service';
import { FocusInfo } from '../tam-dashboard/tam-dashboard.model';
import { DashboardScreenInitializeFocus } from '../../redux/actions/dashboard-screen.actions';
import { SystemUser } from '../../tamalelibs/models/user.model';
import { ScreenService } from '../../tamalelibs/services/screen.service';
import { FiltersConfig, FilterMetadataType } from '../../tamalelibs/components/filter-config';
import { FilterService } from '../../tamalelibs/services/filter.service';
import { EntityService } from '../../tamalelibs/services/entity.service';
import { ScreenMenu } from '../../pages/home-screen/home-screen.model';
import { StoreQuerierService } from '../../services/store-querier.service';
import { visibleScreensSelector } from '../../redux/reducers/system.reducer';
import { AUTO_SAVE_INTERVAL_5 } from '../../tamalelibs/constants/business.constants';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { businessConstants } from '../../constants/business.constants';
import { ScreenHelperService } from '../../services/screen-helper.service';
import { EntityScreenConfig } from './entity-screen.model';
import { AlertButton, AlertBtnTypes } from '../../widgets/alert-window/alert-window.model';
import { researchChangeStatusSelector } from '../../redux/reducers/research-screen.reducer';
import { RelationshipTreeConfig, RelationshipTreeEvents, RelationshipTreeActions } from '../relationship-tree/relationship-tree.view-model';
import { CalendarScreenConfig, CalendarScreenViewType } from '../calendar-screen/calendar-screen.model';
import { CalendarEventsInitializeFocus } from '../../redux/actions/calendar.action';
import { CalendarConstants } from '../../tamalelibs/models/smart-reducer.model';
import { AppState } from '../../redux';
import { PrintService } from '../../services/print.service';
import { SlideSheetActionTypes } from '../slide-sheet/slide-sheet.model';
import { SlideSheetService } from '../../services/slide-sheet.service';
import { DefaultEntityTemplateConfigService } from '../../tamalelibs/services/default-entity-template-config.service';
import { EntityDialogService } from '../entity-dialog/entity-dialog.service';
import { EntityEditStatusActionType } from '../entity-dialog/entity-dialog.model';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';

@Component({
    selector: 'entity-screen',
    templateUrl: './entity-screen.component.html',
    styleUrls: ['./entity-screen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityScreenComponent implements OnInit, OnDestroy {
    @ViewChild('screensMenu', { static: false }) menuEle: ContextMenuComponent<any>;

    currentUser: SystemUser;
    entityScreen: any;
    entityConfig: EntityDetailConfig = new EntityDetailConfig();
    relationshipTreeConfig: RelationshipTreeConfig = new RelationshipTreeConfig();
    isSmallScreenSize = false;
    currentEntity: Entity;
    handlingEntityId: string;

    firstTabPageId: string; // for record the first dashboard tab id
    currentPageId: string;
    actionSubject$: Subject<any> = new Subject(); // loading dashboard when adding new dashboard tab or changing dashboard tab
    entityDetailScreenId = businessConstants.detailsScreenId;
    relationshipScreenId = businessConstants.relationshipScreenId;
    isPrepareDataReady = false;
    isEntityScreenLoading = false;
    loadingSpecificScreen = false; // param for dashboard screens loading
    isRelationshipLoading = false;
    reloadingDashboard = false;

    selectedIndex = 0;
    pages: Array<any> = [];
    menuItems: Array<any> = [{
        title: StringLiteralsPipe.translate('general.detail'),
        disabled: false,
        editable: false,
    }];
    draggableTab = false;
    contextMenuConfig: ContextMenuConfig;
    screenMenuContainer: ScreenMenu[];

    private _destroySubscriptions: Array<Subscription> = [];
    private _subscribed = false;
    private _switchToDetailTab = false;
    private _templateConfigMap: Map<string, any> = new Map();
    private _timer: any;

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _homeScreenService: HomeScreenService,
        private _entityService: EntityService,
        private _screenHelperService: ScreenHelperService,
        private _storeQuerier: StoreQuerierService,
        private _researchHelper: ResearchHelperService,
        private _toast: ToastService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _contextMenuService: ContextMenuService<any>,
        private _slideSheetService: SlideSheetService,
        private _screenService: ScreenService,
        private _alertWindow: AlertWindowService,
        private _entityDialogService: EntityDialogService,
        private _filterService: FilterService,
        private _store: Store<AppState>,
        private _relationshipTreeService: RelationshipTreeService,
        private _printService: PrintService,
    ) { }

    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
        this._createScreenMenu();
        this._initTabContextMenu();
        this._initSubscriptions();
    }

    ngOnDestroy() {
        this._store.dispatch({ type: ScreenActionTypes.SCREEN_STATUS_DICTIONARY_CLEAR });
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this.pages.forEach(element => {
            this._store.dispatch({
                type: this._screenHelperService.getDeleteScreenType(element.screenTpe),
                prefix: element.key
            });
        });
        this._subscribed = false;
    }
    /**
     * add research screen and dashboard screen
     */
    onAddScreen(screen) {
        const pageId = this._createGUID();
        if (screen) {
            const page = {
                id: pageId,
                name: screen.caption,
                screenId: screen.id,
                screenType: screen.type,
                editable: false
            };
            // create related NgRx reducer
            this._store.dispatch({
                type: this._screenHelperService.getAddScreenType(screen.type),
                prefix: page.id
            });
            // add the page to container firstly
            this._addNewTabToContainerAndSetPageIndex(page);
            if (screen.type === businessConstants.screenType.research) {
                this._prepareStoreForResearchScreen(page).pipe(take(1)).subscribe(() => {
                    this.pages[this.selectedIndex]['config'] = new ResearchScreenConfigs(pageId, false);
                    // add new tab to entity screen tabs
                    this._addNewEntityscreenTab(page);
                    this._updateChangesToUI();
                });
            } else if (screen.type === businessConstants.screenType.dashboard) {
                this._prepareStoreForDashboardScreen(page);
                // add new tab to entity screen tabs
                this._addNewEntityscreenTab(page);
                this._updateChangesToUI();
            } else if (screen.type === businessConstants.screenType.event) {
                // set @input parameter config to load Events tab
                this.pages[this.selectedIndex]['config'] = new CalendarScreenConfig(CalendarConstants.ActionKeyName, pageId);
                this.pages[this.selectedIndex]['config'].viewType = CalendarScreenViewType.Month; // set default value for view type
                this._prepareStoreForEventScreen(page);
                // add new tab to entity screen tabs
                this._addNewEntityscreenTab(page);
                this._updateChangesToUI();
            }
        }
    }

    /**
     * the detail tab should be split to two tabs 'details' and 'relationship' when window width is less than 768px.
     * @param event
     */
    onResize(event) {
        this._initRelationshipTabs(event.target.innerWidth);
        this._updateChangesToUI();
    }

    onTabSort(data) {
        this.pages.splice(data.upIndex, 0, this.pages.splice(data.downIndex, 1)[0]);
        this.selectedIndex = data.selectedIndex;
        this._homeScreenService.updateIndividualScreen(this.currentEntity.type.id, this.pages, false, true).pipe(take(1)).subscribe(() => {
            this._updateChangesToUI();
        });
    }

    /**
     * only need  to reload saved screen for dashboard.
     * @param index
     */
    onTabSelectChange(index: number) {
        if (this.selectedIndex !== index) {
            const page = this.pages[index];
            this.currentPageId = page.id;
            this.selectedIndex = index;
            if (page.screenType === businessConstants.screenType.dashboard) {
                this.reloadingDashboard = true;
                const screenInfo = {
                    id: page.screenId,
                    type: page.screenType,
                    storeKey: page.id
                };
                this.loadingSpecificScreen = false;
                this.actionSubject$.next({
                    type: businessConstants.refreshDashboard,
                    payload: screenInfo
                });
            }
            this._updateChangesToUI();
        }
    }

    /**
     * change tab title
     * @param data
     */
    onTabTitleBlur(data) {
        if (data && data.index) {
            this.pages[data.index].editing = false;
            if (data.title.length === 0) {
                return;
            } else if (data.title.length > businessConstants.common.titleMaxLength) {
                const subscription = this._alertWindow.error(
                    businessConstants.tab.renamedFailed.alertTitle, [businessConstants.tab.renamedFailed.alertContent]).subscribe(() => {
                        subscription.unsubscribe();
                    });
            } else {
                if (this.pages[data.index].name !== data.title) {
                    this.pages[data.index].name = data.title;
                    this.pages[data.index].editing = false;
                }
            }
            this._updateChangesToUI();
        }
    }

    /**
     * used for context menu on tabs
     * @param event
     * @param item
     */
    showPageContextMenu(event, item) {
        if (item.screenId && item.screenId.indexOf(businessConstants.homeScreen.prefixNewResearchViewID) === -1) {
            this.contextMenuConfig.items[0].disabled = true;
        } else {
            this.contextMenuConfig.items[0].disabled = false;
        }
        if (item.screenId === businessConstants.detailsScreenId
            || item.screenId === businessConstants.notesScreenId
            || item.screenId === businessConstants.relationshipScreenId) {
            this.contextMenuConfig.items[1].disabled = true;
        } else {
            this.contextMenuConfig.items[1].disabled = false;
        }
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(event, item));
    }

    /**
     * used show the context menu when user click '+'
     * @param event
     * @param item
     */
    showPagesContextMenu(event: MouseEvent, item: any) {
        this._contextMenuService.show(this.menuEle, { x: event.clientX, y: event.clientY });
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * tab change for side sheet mode or entity list page mode
     * @param currentTabIndex
     */
    selectChange(currentTabIndex: number) {
        this.selectedIndex = currentTabIndex;
        this._updateChangesToUI();
    }

    trackByFn(index, item) {
        return item.id;
    }

    private _addNewEntityscreenTab(page) {
        this.loadingSpecificScreen = true;
        this._homeScreenService.updateIndividualScreen(this.currentEntity.type.id, this.pages, false, true).pipe(take(1)).subscribe(() => {
            if (page.screenType === businessConstants.screenType.dashboard) {
                this.loadingSpecificScreen = false;
                this._updateChangesToUI();
                const screenInfo = {
                    id: page.screenId,
                    type: page.screenType,
                    storeKey: page.id
                };
                // add setTimeout to solve the issue that the default entity was not set for a newly added dashboard
                setTimeout(() => {
                    this.actionSubject$.next({
                        type: businessConstants.refreshDashboard,
                        payload: screenInfo
                    });
                });
            } else if (page.screenType === businessConstants.screenType.event) {
                this.loadingSpecificScreen = false;
                this._updateChangesToUI();
            }
        });
    }

    private _addNewTabToContainerAndSetPageIndex(page) {
        const newPage = page;
        newPage['type'] = 'share';
        this.pages.push(newPage);
        this.currentPageId = page.id;
        this.selectedIndex = this.pages.length - 1;
        this._updateChangesToUI();
    }

    private _createScreenMenu() {
        this.screenMenuContainer = [
            new ScreenMenu('01', 'Research View', businessConstants.screenType.research, 'research', null, 'screen_research'),
            new ScreenMenu('02', 'Dashboard', businessConstants.screenType.dashboard, 'dashboard', null, 'screen_dashboard'),
            new ScreenMenu('03', 'Calendar', businessConstants.screenType.event, 'calendar', null, 'screen_calendar')
        ];
    }

    private _clearTimer(): void {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }

    private _clearPrepareDataReady() {
        this.isPrepareDataReady = true;
        this.isRelationshipLoading = false;
        // Need refactor in the future
        // load the events after the entity focus store has been changed.
        setTimeout(() => {
            if (this.currentPageId === businessConstants.eventScreen.eventScreenId) {
                this.pages[this.selectedIndex].config.actions$.next({
                    type: 'RELOAD'
                });
            }
        });
        this._updateChangesToUI();
    }

    private _createGUID() {
        return UUID.UUID().replace(/-/g, '');
    }

    private _initRelationshipTabs(windowWidth: number) {
        if (windowWidth <= 768) {
            this.isSmallScreenSize = true;
            if (!this._isRelationshipTabExisted()) {
                this._addRelationshipTabToPageTabs();
            }
        } else {
            this.isSmallScreenSize = false;
            if (this._isRelationshipTabExisted()) {
                this._removeRelationshipTabToPageTabs();
            }
        }
    }

    private _addRelationshipTabToPageTabs() {
        const relationshipPage = this._getRelationshipTab();
        this.pages.push(relationshipPage);
        this._updateChangesToUI();
    }

    private _getRelationshipTab() {
        const relationshipPage = {};
        relationshipPage['id'] = businessConstants.relationshipScreenId;
        relationshipPage['screenId'] = businessConstants.relationshipScreenId;
        relationshipPage['type'] = 'share';
        relationshipPage['name'] = 'Relationships';
        relationshipPage['screenType'] = 'relationships';
        return relationshipPage;
    }

    private _initSubscriptions() {
        if (!this._subscribed) {
            this._subscribed = true;
            this._destroySubscriptions.push(
                this._store.pipe(
                    select(getEntityScreenSelector),
                    skip(1),
                ).subscribe(entityScreens => this._onSubscribeEntityScreen(entityScreens)),

                this._store.pipe(
                    select(visibleScreensSelector),
                    filter(item => !!item)
                ).subscribe(items => this._loadScreens(items)),

                this._homeScreenService.reloadingAssignedTab$.subscribe((action) => {
                    if (action.type === businessConstants.screenType.dashboard) {
                        this.reloadingDashboard = false;
                        this._updateChangesToUI();
                    }
                }),

                this._screenHelperService.saveScreen$.subscribe(() => {
                    this._saveEntityScreenTabs();
                }),

                // subscribe research change status for auto save
                this._store.pipe(
                    select(researchChangeStatusSelector)
                ).subscribe(researchStatus => {
                    if (researchStatus) {
                        this._onSubscribeResearchChangeStatus(researchStatus);
                    }
                }),

                // refresh entity when edit entity success
                this._entityDialogService.entityEditStatus$.subscribe((event) => {
                    if (event.type === EntityEditStatusActionType.EDIT_ENTITY_CREATE_ENTRY_ALL_SUCCESS) {
                        this._refreshEntityAndRelationshipTree(this.currentEntity.id);
                    }
                }),


                // relationships
                this.relationshipTreeConfig.events$.subscribe((event) => {
                    if (event.type === RelationshipTreeEvents.NODE_SINGLE_CLICKED) {
                        const entity = event.payload;
                        if (entity && entity.nodeType === 'ENTITY') {
                            if (entity.type && entity.type.name === 'Contact') {
                                this._slideSheetService.slideSheetActionSubject$.next({
                                    type: SlideSheetActionTypes.CONTACT_PANEL,
                                    payload: entity.id
                                });
                            } else {
                                this._slideSheetService.slideSheetActionSubject$.next({
                                    type: SlideSheetActionTypes.ENTITY_PANEL,
                                    payload: entity.id
                                });
                            }
                        }
                        // need to triger auto save if the format of display name is changed
                    } else if (event.type === RelationshipTreeEvents.CONTEXTMENU_SHOWNAMEFORMAT) {
                        // create a configuration for relationsihp tree on detail page
                        const configuration = {
                            relationshipTreeInfo: {
                                showAs: event.payload
                            }
                        };
                        // update the configuration to the pages
                        this.pages.forEach(item => {
                            if (item.screenKey === businessConstants.detailsScreenId) {
                                item[businessConstants.common.configuration] = configuration;
                            }
                        });
                        // trigger auto save to save the name format changing on relationshp tree
                        this._store.dispatch({
                            type: ResearchScreenActionTypes.SET_HOME_SCREEN_CHANGE_STATUS,
                            payload: {
                                screenKey: businessConstants.detailsScreenId,
                                component: businessConstants.componentType.entityDetail
                            }
                        });
                    }
                }),

                this._relationshipTreeService.relationshipTreeOperate$.subscribe(action => {
                    if (action.type === RelationshipTreeActions.REMOVE_RELATIONSHIP
                        || action.type === RelationshipTreeActions.ADD_RELATIONSHIP
                        || action.type === RelationshipTreeActions.CHANGE_RELATIONSHIP) {
                        this._refreshEntityAndRelationshipTree(this.currentEntity.id);
                    }
                }),

                this._printService.actionSubject$.subscribe(action => {
                    if (this.pages[this.selectedIndex].screenType === 'research' && this.pages[this.selectedIndex].config) {
                        this.pages[this.selectedIndex].config.actionSubject$.next(action);
                    }
                }),

                this._researchHelper.researchActionSubject$.subscribe(action => {
                    if (this.pages[this.selectedIndex].screenType === 'research' && this.pages[this.selectedIndex].config) {
                        this.pages[this.selectedIndex].config.actionSubject$.next(action);
                    }
                })
            );

            // get entity informaiton via entityID of url param
            if (this._route.params) {
                this._destroySubscriptions.push(
                    this._route.params.subscribe((params) => {
                        if (params['id']) {
                            this._clearTimer();
                            // reset state
                            this.pages = [];
                            this.isPrepareDataReady = false;
                            this.isEntityScreenLoading = true;
                            // tabs draggable for all users
                            this.draggableTab = true;
                            this.selectedIndex = 0;
                            this._refresh(params['id']);
                        }
                    })
                );
            }
        }
    }

    private _initTabContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.single = true;
        this.contextMenuConfig.showItemIcon = false;
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.items = [
            { id: '0', text: 'Rename Tab' },
            { id: '1', text: 'Close Tab' }
        ];
        this._destroySubscriptions.push(
            this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)),
        );
    }

    private _isRelationshipTabExisted() {
        let existed = false;
        const relationshipTab = this.pages.filter(page => page.screenId === businessConstants.relationshipScreenId);
        if (relationshipTab && relationshipTab.length > 0) {
            existed = true;
        }
        return existed;
    }

    private _loadScreens(screenList) {
        this._homeScreenService.setScreenMenuItems(screenList, this.screenMenuContainer, true);
        this._updateChangesToUI();
    }

    private _onMenuSelect(event) {
        const actionId = event.payload.id;
        const data = event.payload.data;
        switch (actionId) {
            case '0':
                data.editing = true;
                this._updateChangesToUI();
                break;
            case '1':
                if (this.pages.length > 1) {
                    const deleteIndex = this.pages.indexOf(data);
                    const isChangeTab = deleteIndex === this.selectedIndex;
                    if (this.selectedIndex >= deleteIndex && this.selectedIndex !== 0) {
                        this.selectedIndex--;
                    }
                    let deleteTitle = '';
                    if (this.pages[deleteIndex]) {
                        deleteTitle = this.pages[deleteIndex].name;
                    }
                    this.pages.splice(deleteIndex, 1);
                    this._homeScreenService.updateIndividualScreen(this.currentEntity.type.id, this.pages, false, true).pipe(take(1)).subscribe(() => {
                        this._updateChangesToUI(); // update the UI when the tab was deleted successfuly
                        const msg = deleteTitle + businessConstants.entityScreen.deleteTabMsg;
                        // popup a toast notification
                        this._toast.notify({
                            message: msg,
                            style: NotificationStyles.Success
                        });

                        const page = this.pages[this.selectedIndex];
                        this.currentPageId = this.pages[this.selectedIndex].id;
                        if (isChangeTab && page) {
                            if (page.screenType === businessConstants.screenType.dashboard) {
                                const screenInfo = {
                                    id: page.screenId,
                                    type: page.screenTypebusinessConstants,
                                    storeKey: page.id
                                };
                                this.loadingSpecificScreen = false;
                                this.actionSubject$.next({
                                    type: 'REFRESH_DASHBOARD',
                                    payload: screenInfo
                                });
                            } else if (page.screenType === businessConstants.screenType.event) {
                                page.config.actions$.next({
                                    type: 'RELOAD'
                                });
                            }
                            this._updateChangesToUI(); // This may be unnecessary, it should be triggered in the subscribe.
                        }
                    });
                }
                break;
            default:
                break;
        }
    }

    /**
     * distinct the pages in case the pages are duplicated
     * @param pageId
     */
    private _isPageExisted(pageId) {
        let result = false;
        const filterItem = this.pages.filter(page => page.id === pageId);
        if (filterItem && filterItem.length > 0) {
            result = true;
        }
        return result;
    }

    private _onSubscribeEntityScreen(_entityScreen) {
        if (_entityScreen && _entityScreen.tabs) {
            this.isPrepareDataReady = false;
            this.entityScreen = _entityScreen;
            const pages = this.entityScreen.tabs;
            // if the dashboard is the first tab in entityscreen, it will be loaded when loading entity screen.
            if (pages && pages.length > 0 && pages[0].screenType === businessConstants.screenType.dashboard) {
                this.firstTabPageId = pages[0].id;
            }
            pages.forEach(page => {
                page.screenKey = page.id;
                if (page.screenType === businessConstants.screenType.research) {
                    page['config'] = new ResearchScreenConfigs(page.id, false);
                }
                if (page.screenKey === businessConstants.detailsScreenId) {
                    if (page.configuration && page.configuration.relationshipTreeInfo) {
                        this.relationshipTreeConfig.showAs = page.configuration.relationshipTreeInfo.showAs;
                    }
                }
                if (!this._isPageExisted(page.id)) {
                    this.pages.push(page);
                }
            });
            this._prepareStores(pages).pipe(
                switchMap(() => {
                    if (this._switchToDetailTab) {
                        this.selectedIndex = this._getEntityDetailTabIndex();
                    }
                    this.currentPageId = this.pages[this.selectedIndex].id;
                    this._clearPrepareDataReady();
                    return of(null);
                }),
                take(1),
            ).subscribe(() => {
                this._showActionMenu(this.entityConfig.entity.id);
            });
        }
    }

    /**
     * get adhoc value of the entity
     * @param _config
     */
    private _onSubscribeTemplateConfig(_config: any): void {
        if (_config && !this._templateConfigMap.has(this.currentEntity.id)) {
            this._templateConfigMap.set(this.currentEntity.id, _config);
        }
        this.entityConfig.customMetadata = [];
        const temp = this._templateConfigMap.get(this.currentEntity.id);
        if (temp) {
            this.entityConfig.customMetadata = this._entityService.getCustomMetadata(this.currentEntity, temp);
            this._updateChangesToUI();
        }
    }

    private _onSubscribeResearchChangeStatus(researchStatus) {
        if (researchStatus.type === ResearchScreenActionTypes.SET_HOME_SCREEN_CHANGE_STATUS &&
            researchStatus.payload) {
            this._clearTimer();
            this._timer = setTimeout(() => {
                const component = researchStatus.payload.component;
                const screenKey = researchStatus.payload.screenKey;

                if (component !== ResearchScreenSubComponents.KEYWORD) {
                    this._homeScreenService.updateIndividualScreen(this.currentEntity.type.id, this.pages, screenKey, true).pipe(take(1)).subscribe(() => {
                        // save change research view to store
                        this._store.dispatch({ type: ScreenActionTypes.SCREEN_STATUS_DICTIONARY, payload: screenKey });
                        this._store.dispatch({
                            type: ResearchScreenActionTypes.SET_HOME_SCREEN_CHANGE_STATUS,
                            payload: false
                        });
                    });
                }
                this._updateChangesToUI();
            }, AUTO_SAVE_INTERVAL_5);
        }
    }

    private _prepareStores(pages): Observable<any> {
        const observables$: Array<Observable<any>> = [];
        pages.forEach(element => {
            if (element.screenId !== businessConstants.detailsScreenId) {
                // create related NgRx reducer
                if (element.id) {
                    this._store.dispatch({
                        type: this._screenHelperService.getAddScreenType(element.screenType),
                        prefix: element.id
                    });
                    // set edit permission for tab
                    if (element.screenType === businessConstants.screenType.event) {
                        element.config = new CalendarScreenConfig(CalendarConstants.ActionKeyName, element.id);
                        element.config.viewType = CalendarScreenViewType.Month;
                        observables$.push(this._prepareStoreForEventScreenObservable(element));
                    } else if (element.screenType === businessConstants.screenType.dashboard) {
                        // put the dashboard screen to observables
                        observables$.push(this._prepareStoreForDashboardScreenObservable(element));
                    } else {
                        // init the config for <research-screen> embeded in ng-for
                        element.config = new ResearchScreenConfigs(element.id, false);
                        observables$.push(
                            this._prepareStoreForResearchScreen(element).pipe(
                                catchError(err => {
                                    return of(null);
                                }),
                                take(1),
                            )
                        );
                    }
                }
            }
        });
        return forkJoin(observables$);
    }

    /**
     * prepare dashboard store
     * @param page
     */
    private _prepareStoreForDashboardScreen(page) {
        // put the dashboard screen to observables
        let focusInfo = new FocusInfo();
        const entityBrief = new EntityBrief(this.currentEntity.id, this.currentEntity.name);
        entityBrief.shortName = this.currentEntity.shortName;
        focusInfo = { entity: entityBrief };
        this._store.dispatch(new DashboardScreenInitializeFocus(page.id, focusInfo));
    }

    /**
     * create observable for prepare dashboard store
     * @param page
     */
    private _prepareStoreForDashboardScreenObservable(page): Observable<any> {
        let focusInfo = new FocusInfo();
        const entityBrief = new EntityBrief(this.currentEntity.id, this.currentEntity.name);
        entityBrief.shortName = this.currentEntity.shortName;
        focusInfo = { entity: entityBrief };
        const observable = new Observable(subscriber => {
            subscriber.next();
            subscriber.complete();
        });
        return observable.pipe(
            switchMap(() => {
                this._store.dispatch(new DashboardScreenInitializeFocus(page.id, focusInfo));
                return of(null);
            })
        );
    }

    private _prepareStoreForEventScreen(page) {
        let payload = new Entity();
        if (this.currentEntity) {
            payload = this.currentEntity;
        }

        this._store.dispatch(new CalendarEventsInitializeFocus(payload, CalendarConstants.ActionKeyName, page.id));
    }

    private _prepareStoreForEventScreenObservable(page): Observable<any> {
        let payload = new Entity();
        if (this.currentEntity) {
            payload = this.currentEntity;
        }
        const observable = new Observable(subscriber => {
            subscriber.next();
            subscriber.complete();
        });
        return observable.pipe(
            switchMap(() => {
                this._store.dispatch(new CalendarEventsInitializeFocus(payload, CalendarConstants.ActionKeyName, page.id));
                return of(null);
            })
        );
    }

    private _prepareStoreForResearchScreen(page): Observable<any> {
        const screenId = page.screenId; // set screenId for loading the screens
        const storeKey = page.id; // set pageId for store the data

        const observable: Observable<any> = new Observable(subscriber => {
            subscriber.next();
            subscriber.complete();
        });
        // put the research screen to observables
        return observable.pipe(
            concatMap(() => {
                if (screenId === businessConstants.allNotesScreenId) {
                    if (!page.configuration || (page.configuration && JSON.stringify(page.configuration) === '{}')) {
                        page.configuration = new ResearchViewInfo();
                    }
                    page.configuration.finderInfo = {
                        entityId: this.currentUser.id,
                        status: ResearchFocusStatus.ALL
                    };
                    page.configuration.relationshipTreeInfo = {
                        checkedNodeKeys: [],
                        showAs: businessConstants.entity.shortName,
                    };
                    page.configuration.layoutInfo.isEntityFocusHidden = true;
                    page.configuration.layoutInfo.isRelationshipTreeHidden = true;
                    page.configuration.layoutInfo.isEntityFocusDisable = true;
                    page.configuration.layoutInfo.isRelationshipTreeDisable = true;
                    return of(page.configuration);
                } else if (screenId === businessConstants.myNotesScreenId) {
                    if (!page.configuration || (page.configuration && JSON.stringify(page.configuration) === '{}')) {
                        page.configuration = new ResearchViewInfo();
                    }
                    page.configuration.finderInfo = {
                        entityId: this.currentUser.id,
                        status: ResearchFocusStatus.NONE
                    };
                    page.configuration.relationshipTreeInfo = {
                        checkedNodeKeys: [],
                        showAs: businessConstants.entity.shortName,
                    };
                    page.configuration.layoutInfo.isEntityFocusHidden = true;
                    page.configuration.layoutInfo.isRelationshipTreeHidden = true;
                    page.configuration.layoutInfo.isEntityFocusDisable = true;
                    page.configuration.layoutInfo.isRelationshipTreeDisable = true;
                    return of(page.configuration);
                } else if (screenId === businessConstants.notesScreenId) {
                    if (!page.configuration || (page.configuration && JSON.stringify(page.configuration) === '{}')) {
                        page.configuration = new ResearchViewInfo();
                        page.configuration.finderInfo = {
                            entityId: this.currentEntity.id,
                            status: ResearchFocusStatus.NONE
                        };
                        page.configuration.relationshipTreeInfo = {
                            checkedNodeKeys: [],
                            showAs: businessConstants.entity.shortName,
                        };
                        page.configuration.layoutInfo.isEntityFocusHidden = true;
                        page.configuration.layoutInfo.isRelationshipTreeHidden = true;
                    }
                    return of(page.configuration);
                } else {
                    if (screenId) {
                        return this._screenService.getById(screenId);
                    } else {
                        return of(page.configuration);
                    }
                }
            }),
            switchMap(response => {
                // 1. loading the configuration from current page if existing;
                // 2. loading and converting the configuration by screenId if the configuration version is 2.0;
                // 3. loading the configuration by screenId;
                if (page.configuration && JSON.stringify(page.configuration) !== '{}') {
                    if (page.configuration.version === '2.0') {
                        return this._homeScreenService.convertResearchViewInfoV2toV3(page.configuration, page.screenId);
                    } else {
                        if (page.configuration) {
                            this._homeScreenService.SetFocusRelationshipDiablesStatus(page.configuration, page.screenId);
                        }
                        // Fixed for TAM-31662: search term within notes list should not be sticky.
                        if (response.configuration) {
                            page.configuration.keywordInfo = response.configuration.keywordInfo;
                        } else {
                            page.configuration.keywordInfo = '';
                        }
                        return of(page.configuration);
                    }
                } else {
                    if (response) {
                        if (response.configuration && response.configuration.version === '2.0') {
                            return this._homeScreenService.convertResearchViewInfoV2toV3(response.configuration, response.screenId);
                        } else {
                            if (response.configuration) {
                                this._homeScreenService.SetFocusRelationshipDiablesStatus(response.configuration, response.screenId);
                            }
                            return of(response.configuration);
                        }
                    } else {
                        const configuration = new ResearchViewInfo();
                        const finderInfo: FinderInfo = {
                            entityId: this.currentUser.id,
                            status: ResearchFocusStatus.ALL
                        };
                        configuration.finderInfo = finderInfo;
                        configuration.relationshipTreeInfo = {
                            checkedNodeKeys: [],
                            showAs: 'shortName',
                        };
                        this._homeScreenService.SetFocusRelationshipDiablesStatus(configuration, page.screenId);
                        return of(configuration);
                    }
                }
            }),
            map((researchViewInfo: ResearchViewInfo) => {
                if (researchViewInfo && researchViewInfo.filterInfo) {
                    if (researchViewInfo.filterInfo.filterConfigs) {
                        researchViewInfo.filterInfo.filterConfigs.forEach((config: FiltersConfig) => {
                            config.operators = this._filterService.getOperatorOptions(config.metadataType);
                            config.source = this._filterService.generateFilterSource(config.metadataType);
                        });
                    }
                    return researchViewInfo;
                } else {
                    researchViewInfo.filterInfo = new FilterInfo();
                    return researchViewInfo;
                }
            }),
            switchMap((researchViewInfo: ResearchViewInfo) => {
                if (researchViewInfo && researchViewInfo.filterInfo) {
                    if (!researchViewInfo.filterInfo.filterConfigs || researchViewInfo.filterInfo.filterConfigs.length === 0) {
                        return of(researchViewInfo);
                    } else {
                        const entityIds = [];
                        researchViewInfo.filterInfo.filterConfigs.forEach(item => {
                            if (item.metadataType === FilterMetadataType.Entities
                                || item.metadataType === FilterMetadataType.Source
                                || item.metadataType === FilterMetadataType.Submitter) {
                                item.value.forEach(val => {
                                    entityIds.push(val.id);
                                });
                            }
                        });
                        if (entityIds.length === 0) {
                            return of(researchViewInfo);
                        } else {
                            return this._entityService.getEntityListByIdsQuick(entityIds).pipe(
                                map(response => this._entityService.mapEntityBriefList(response)),
                                map(entities => {
                                    const entityMap = new Map();
                                    entities.forEach(element => entityMap.set(element.id, element));
                                    return entityMap;
                                }),
                                map(entityMap => {
                                    researchViewInfo.filterInfo.filterConfigs.forEach(item => {
                                        if (item.metadataType === FilterMetadataType.Entities
                                            || item.metadataType === FilterMetadataType.Source
                                            || item.metadataType === FilterMetadataType.Submitter) {
                                            item.value = item.value.map(element => {
                                                if (entityMap.has(element.id)) {
                                                    return entityMap.get(element.id);
                                                } else {
                                                    return element;
                                                }
                                            });
                                        }
                                    });
                                    return researchViewInfo;
                                })
                            );
                        }
                    }
                } else {
                    return of(researchViewInfo);
                }
            }),
            switchMap((researchViewInfo: ResearchViewInfo) => {
                // layout
                this._store.dispatch(new ResearchScreenInitializeLayout(storeKey, researchViewInfo.layoutInfo));
                // display
                this._store.dispatch(new ResearchScreenSetDisplayInfo(storeKey, researchViewInfo.displayInfo));
                // grid status
                this._store.dispatch(new ResearchScreenThreadListSetGridStatus(storeKey, researchViewInfo.gridColumnInfo));
                // grid group layout
                this._store.dispatch(new ResearchScreenThreadListSetGridRowModelType(storeKey, researchViewInfo.gridGroupLayout));
                // sort info
                this._store.dispatch(new ResearchScreenSetSortInfo(storeKey, researchViewInfo.sortInfo));
                // filter
                this._store.dispatch(new ResearchScreenSetFilterList(storeKey, researchViewInfo.filterInfo.filterConfigs));
                this._store.dispatch(new ResearchScreenSetFilterEnable(storeKey, researchViewInfo.filterInfo.isFilterOn));
                this._store.dispatch(new ResearchScreenSetFilterLogic(storeKey, researchViewInfo.filterInfo.filterLogic));
                // keyword
                this._store.dispatch(new ResearchScreenSetKeyWord(storeKey, researchViewInfo.keywordInfo));
                // refineby
                if (researchViewInfo.refineByInfo) {
                    this._store.dispatch(new ResearchScreenRefinebySetCategory(storeKey, researchViewInfo.refineByInfo.category));
                }
                // entity focus
                let finderSetting: FinderSetting;
                if (researchViewInfo.finderInfo) {
                    if (screenId === businessConstants.myNotesScreenId) {
                        finderSetting = {
                            entity: EntityBrief.parse(this.currentUser),
                            status: researchViewInfo.finderInfo.status
                        };
                    } else {
                        finderSetting = {
                            entity: this.currentEntity,
                            status: researchViewInfo.finderInfo.status
                        };
                    }
                } else {
                    finderSetting = {
                        entity: this.currentEntity,
                        status: ResearchFocusStatus.NONE
                    };
                }
                this._store.dispatch(new ResearchScreenInitializeFocus(storeKey, finderSetting));
                // relationship tree (depends on entity focus)
                const nodeData = RelationshipTreeService.parseToTreeViewModel(this.currentEntity);
                const expandedKeys = nodeData.filter(node => !node.isLeaf).map(node => node.uuid);
                let checkedKeys = [];
                if (finderSetting) {
                    if (finderSetting.status === ResearchFocusStatus.ONE_DEGREE) {
                        checkedKeys = nodeData.map(node => node.uuid);
                    } else {
                        if (researchViewInfo && researchViewInfo.relationshipTreeInfo && researchViewInfo.relationshipTreeInfo.checkedNodeKeys) {
                            checkedKeys = this._researchHelper.restoreCheckedKeys(researchViewInfo.relationshipTreeInfo.checkedNodeKeys, nodeData);
                        }
                    }
                }
                if (researchViewInfo.relationshipTreeInfo) {
                    this._store.dispatch(new ResearchScreenSetRelationship(storeKey, {
                        nodes: nodeData,
                        expandedKeys: expandedKeys,
                        checkedKeys: checkedKeys,
                        showAs: researchViewInfo.relationshipTreeInfo.showAs
                    }));
                } else {
                    this._store.dispatch(new ResearchScreenSetRelationship(storeKey, {
                        nodes: nodeData,
                        expandedKeys: expandedKeys,
                        checkedKeys: checkedKeys,
                        showAs: 'shortName'
                    }));
                }
                return of(researchViewInfo);
            }),
            delay(0), // must add delay(0), to confirm all dispatch has been called, and store has been update
        );
    }

    private _removeRelationshipTabToPageTabs() {
        if (this.pages && this.pages.length > 0) {
            let relationshipIndex = 0;
            let ongoing = true;
            this.pages.forEach((page) => {
                if (ongoing) {
                    if (page.screenId !== businessConstants.relationshipScreenId) {
                        relationshipIndex++;
                    } else {
                        ongoing = false;
                    }
                }
            });
            if (relationshipIndex !== this.pages.length) {
                this.pages.splice(relationshipIndex, 1);
            }
            this._updateChangesToUI();
        }
    }

    private _refresh(entityId: string) {
        this._entityService.getEntityDetail(entityId).pipe(
            map(response => {
                return Entity.parse(response, true);
            }),
            catchError(err => {
                this._router.navigate(['/error']);
                return of(null);
            }),
            take(1),
        ).subscribe(res => this._onSubscribeEntity(res));
    }

    private _refreshEntityAndRelationshipTree(_entityId: string) {
        this.isRelationshipLoading = true;
        this._updateChangesToUI();
        this._entityService.getEntityDetail(_entityId).pipe(
            catchError(err => {
                this._router.navigate(['/error']);
                return of(null);
            }),
            take(1),
        ).subscribe((response) => {
            if (response) {
                this.currentEntity = Entity.parse(response);
                this.entityConfig.entity = Object.assign({}, this.currentEntity);
                delete this.entityConfig.entity.relationships;
                delete this.entityConfig.entity.relationshipNode;
                this._onSubscribeTemplateConfig(null);

                const treeNodes = RelationshipTreeService.parseToTreeViewModel(this.currentEntity);
                this.relationshipTreeConfig.nodes = treeNodes;
                this.relationshipTreeConfig.showAs = 'shortName';
                this.relationshipTreeConfig.expandedKeys = [];
                treeNodes.forEach(element => {
                    if (element.uuid !== '1' && element.level !== '4') {
                        this.relationshipTreeConfig.expandedKeys.push(element.uuid);
                    }
                });
                this.isRelationshipLoading = false;
                this._updateChangesToUI();
            }
        });
    }

    private _saveEntityScreenTabs() {
        // don't need to save relationship tab
        this._removeRelationshipTabToPageTabs();

        const cancelBtn: AlertButton = {
            text: 'Cancel',
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: 'Save',
            type: AlertBtnTypes.primary
        };
        const subscription = this._alertWindow.custom(`Layout changes will be applied for entity type ${this.currentEntity.type.name}`, ['Are you sure you want to save your changes?'], cancelBtn, confirmButton, 500).subscribe((result: boolean) => {
            if (result) {
                const entityScreenInfo = new EntityScreenConfig();
                entityScreenInfo.id = this.entityScreen['id'];
                entityScreenInfo.description = this.entityScreen['description'];
                entityScreenInfo.type = this.entityScreen['type'];
                entityScreenInfo.name = this.entityScreen['name'];
                entityScreenInfo.isPublic = true;
                entityScreenInfo.tabs = this.pages;
                this._homeScreenService.updateEntityScreenTabs(entityScreenInfo, true).pipe(take(1)).subscribe(() => {
                    const msg = `Layout successfully applied to ${this.currentEntity.type.name}.`;
                    // popup a toast notification
                    this._toast.notify({
                        message: msg,
                        style: NotificationStyles.Success
                    });
                });
            }
            subscription.unsubscribe();
        });
    }

    private _getEntityDetailTabIndex() {
        let tabIndex = 0;
        if (this.pages.length > 0) {
            tabIndex = this.pages.findIndex((value, index, arr) => {
                return value.screenId === businessConstants.detailsScreenId;
            });
        }
        return tabIndex;
    }

    private _getEntityScreen(entityTypeId: string) {
        this._store.dispatch({
            type: EntityScreenActionTypes.LOAD,
            payload: entityTypeId
        });
    }

    private _onSubscribeEntity(_entity: Entity): void {
        if (_entity) {
            this.currentEntity = _entity;
            this._getEntityScreen(_entity.type.id);
            this._showEntityOnPage(this.currentEntity);
            if (this._templateConfigMap.has(_entity.id)) {
                this._onSubscribeTemplateConfig(null);
            } else {
                this._entityService.getEntityTemplateConfig(_entity.type.id).pipe(
                    map(response => {
                        return response === null ? DefaultEntityTemplateConfigService.getDefaultEntityTemplateConfig() : response;
                    }),
                    catchError(err => {
                        return of(null);
                    }),
                    take(1),
                ).subscribe(res => {
                    if (res) {
                        this._onSubscribeTemplateConfig(res);
                    }
                });
            }
        }
    }

    private _showEntityOnPage(_entity: Entity): void {
        this.entityConfig.entity = _entity;
        this._refreshRelationships();
        this._updateChangesToUI();
    }

    private _refreshRelationships() {
        this._initRelationshipTabs(window.innerWidth);
        this.relationshipTreeConfig.checkable = false;
        this.relationshipTreeConfig.expandable = true;
        const treeNodes = RelationshipTreeService.parseToTreeViewModel(this.currentEntity);
        this.relationshipTreeConfig.nodes = treeNodes;
        this.relationshipTreeConfig.showAs = 'shortName';
        this.relationshipTreeConfig.expandedKeys = [];
        treeNodes.forEach(element => {
            if (element.uuid !== '1' && element.level !== '4') {
                this.relationshipTreeConfig.expandedKeys.push(element.uuid);
            }
        });
        this.isRelationshipLoading = false;
    }

    private _showActionMenu(entityId: string) {
        const currentPageInfo: any = {
            url: this._route.snapshot.url,
            id: entityId,
            name: '',
            screenType: 'Entity Detail'
        };
        currentPageInfo.type = 'Entity Detail';
        this._store.dispatch({
            type: ScreenActionTypes.SET_VALUE,
            payload: currentPageInfo
        });
    }

    private _updateChangesToUI() {
        if (!this._changeDetectorRef[businessConstants.common.changeDetectorDestroyedIdentifier]) {
            this._changeDetectorRef.detectChanges();
        }
    }
}
